import { AUTO_STYLE, keyframes, style } from '@angular/animations';
import { Directive, ElementRef, Input, OnInit } from '@angular/core';
import { Util } from '@libs/utilities/util';

import { NgTimedAnimationService } from '../../shared/services/timed-animation.service';
import { EnumNgEventName } from '../../shared/event-animation.data';
import { INgAnimationEffectOptions, INgAnimationStates } from '../../shared/event-animation.interface';


/**------------------------------------------------------
 * Animation State Definitions
 */
function wobbleVertical(translateY: number | undefined): INgAnimationStates {

	//0 - set the default if no specific value is given
	translateY = translateY ? translateY : 6;

	//1 - make transition steps by number of transition state steps
	const difference 	: number = Util.Number.roundDouble(translateY / 6); 						// divide the translateY value by total number of transition states
	const translateY1 	: number = Util.Number.roundDouble(translateY);
	const translateY2 	: number = Util.Number.roundDouble(translateY1 - difference);
	const translateY3 	: number = Util.Number.roundDouble(translateY2 - difference);
	const translateY4 	: number = Util.Number.roundDouble(translateY3 - difference);
	const translateY5 	: number = Util.Number.roundDouble(translateY4 - difference);
	const translateY6 	: number = Util.Number.roundDouble(translateY5 - difference);
	const translateY7 	: number = Util.Number.roundDouble(translateY6 - difference);

	//2 - create the animations state
	const animationStates: INgAnimationStates = {
		start 	: keyframes([
			style({
				visibility 	: AUTO_STYLE,
				transform 	: 'translateY(0)',
				easing 		: 'ease',
				offset 		: 0
			}),
			style({
				transform 	: `translateY(${Util.Number.toPositive(translateY1)}px)`,
				easing 		: 'ease',
				offset 		: 0.083
			}),
			style({
				transform 	: `translateY(${Util.Number.toPositive(translateY2)}px)`,
				easing 		: 'ease',
				offset 		: 0.165
			}),
			style({
				transform 	: `translateY(-${Util.Number.toPositive(translateY3)}px)`,
				easing 		: 'ease',
				offset 		: 0.330
			}),
			style({
				transform 	: `translateY(${Util.Number.toPositive(translateY4)}px)`,
				easing 		: 'ease',
				offset 		: 0.495
			}),
			style({
				transform 	: `translateY(-${Util.Number.toPositive(translateY5)}px)`,
				easing 		: 'ease',
				offset 		: 0.660
			}),
			style({
				transform 	: `translateY(${Util.Number.toPositive(translateY6)}px)`,
				easing 		: 'ease',
				offset 		: 0.825
			}),
			style({
				transform 	: `translateY(-${Util.Number.toPositive(translateY7)}px)`,
				easing 		: 'ease',
				offset 		: 1
			})
		])
	};
	return animationStates;
}


/**------------------------------------------------------
 * Wobble Vertical on Event
 */
@Directive({
    selector: '[ngWobbleVerticalOnEvent]',
    providers: [NgTimedAnimationService],
    standalone: false
})
export class NgWobbleVerticalOnEventDirective implements OnInit {

	//** Input Decorators */
	@Input() effectOptions: Partial<INgCustomAnimationEffectOptions> = null!;
	@Input() targetElement: HTMLElement 						   = null!;

	constructor(
		private elementRef 				: ElementRef,
		private timedAnimationService 	: NgTimedAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the translateY value is relative to the animation functionality
		if (this.effectOptions?.translateY && this.effectOptions?.translateY < 0) throw new Error(`WobbleVerticalOnEventDirective => ngOnInit => FATAL ERROR: Provided translateY value should be positive number: ${this.effectOptions?.translateY}`);

		//1 - setup the animation
		this.timedAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: wobbleVertical(this.effectOptions?.translateY),
			event			: this.effectOptions?.event!,
			timing			: {
				duration	: this.effectOptions?.duration,
				delay		: this.effectOptions?.delay,
				timeout		: this.effectOptions?.timeout || this.effectOptions?.duration
			}
		});
	}
}


/**------------------------------------------------------
 * wobble Vertical on Mouse Hover
 */
@Directive({
    selector: '[ngWobbleVerticalOnMouseHover]',
    providers: [NgTimedAnimationService],
    standalone: false
})
export class NgWobbleVerticalOnMouseHoverDirective implements OnInit {

	//** Input Decorators */
	@Input() effectOptions: Partial<INgCustomAnimationEffectOptions> = null!;
	@Input() targetElement: HTMLElement 						   = null!;

	constructor(
		private elementRef 				: ElementRef,
		private timedAnimationService 	: NgTimedAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the translateY value is relative to the animation functionality
		if (this.effectOptions?.translateY && this.effectOptions?.translateY < 0) throw new Error(`WobbleVerticalOnMouseHoverDirective => ngOnInit => FATAL ERROR: Provided translateY value should be positive number: ${this.effectOptions?.translateY}`);

		//1 - setup the animation
		this.timedAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: wobbleVertical(this.effectOptions?.translateY),
			event			: EnumNgEventName.MouseHover,
			timing			: {
				duration	: this.effectOptions?.duration,
				delay		: this.effectOptions?.delay,
				timeout 	: this.effectOptions?.timeout || this.effectOptions?.duration
			}
		});
	}
}


//** Interfaces --------------------------------- */
interface INgCustomAnimationEffectOptions extends INgAnimationEffectOptions {
	translateY : number;
}
